import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import ModuleQuiz from "../components/ModuleQuiz";
import ModuleChecklist from "../components/ModuleChecklist";
import ProjectFolderPicker from "../components/ProjectFolderPicker";
import { MODULE_SUMMARIES } from "../data/moduleSummaries";
import { getChecklist } from "../data/moduleChecklists";
import { api, formatError, API } from "../lib/api";
import { normalizeVideoUrl } from "../components/IntroSection";
import { Document as PdfDocument, Page as PdfPage, pdfjs as pdfjsLib } from "react-pdf";
import { cn } from "../lib/utils";
import { sanitizeVideoEmbed } from "../lib/sanitizeEmbed";
import { useVideoDuration } from "../hooks/useVideoDuration";

// Configure the pdf.js worker once at module load. Self-hosted from
// /public/pdf.worker.min.js so there's zero third-party CDN dependency.
try {
  pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
} catch (_) { /* older/newer builds may name it differently */ }
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, PlayCircle, ExternalLink, Lock, BookOpen, FileText, Download, Award, ChevronRight, Trophy, ClipboardCheck, CircleCheck, ListChecks, FolderPlus, Loader2, ClipboardList, CheckCircle2, Maximize2, Minimize2 } from "lucide-react";
import AdaptedText from "@/components/AdaptedText";

// Per-module non-backend metadata (lesson count, duration, tools) that isn't
// stored on the backend module doc yet. Worksheets are NOT hardcoded here —
// they're pulled live from /api/modules/{slug}/worksheets (attached via the
// admin Worksheets console).
const MODULE_EXTRAS = {
  "company-legal-setup": {
    tools: [{ name: "Value Chain Calculator", to: "/tools/value-chain" }],
    lessons: 5, duration: "1h 45m",
  },
  "idea-founder-readiness": {
    tools: [], lessons: 6, duration: "1h 55m",
  },
  "product-development": {
    tools: [{ name: "Retail Margin Calculator", to: "/tools/retail-margin" }],
    lessons: 6, duration: "2h 10m",
  },
  "brand-strategy": {
    tools: [], lessons: 6, duration: "2h 00m",
  },
  "go-to-market": {
    tools: [{ name: "CPG Sales Estimator", to: "/tools/sales-estimator" }],
    lessons: 9, duration: "3h 10m",
  },
  "distribution": {
    tools: [], lessons: 7, duration: "2h 20m",
  },
  "trade-marketing": {
    tools: [{ name: "Value Chain Calculator", to: "/tools/value-chain" }],
    lessons: 8, duration: "2h 45m",
  },
  "consumer-marketing": {
    tools: [], lessons: 8, duration: "2h 35m",
  },
  "operations-team": {
    tools: [], lessons: 6, duration: "1h 50m",
  },
  "scaling-exit": {
    tools: [], lessons: 9, duration: "3h 00m",
  },
  // Bonus modules
  "ai-tools": {
    tools: [], lessons: 7, duration: "2h 15m", bonus: true,
  },
  "broker-masterclass": {
    tools: [], lessons: 8, duration: "2h 40m", bonus: true,
  },
  "retail-buyer-masterclass": {
    tools: [], lessons: 9, duration: "3h 05m", bonus: true,
  },
  "cogs-financials": {
    tools: [
      { name: "Value Chain Calculator", to: "/tools/value-chain" },
      { name: "Retail Margin Calculator", to: "/tools/retail-margin" },
    ],
    lessons: 10, duration: "3h 30m", bonus: true,
  },
  "capital-raise": {
    tools: [], lessons: 8, duration: "2h 50m", bonus: true,
  },
  "market-testing": {
    tools: [{ name: "CPG Sales Estimator", to: "/tools/sales-estimator" }],
    lessons: 7, duration: "2h 20m", bonus: true,
  },
};

export default function ModuleDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Structured 403 from paid-user sequential progression. When set, the
  // page renders a "Module Locked — Complete Module X to unlock Module
  // Y" gate with a "Go to Module X" button instead of the module body.
  const [lockedGate, setLockedGate] = useState(null);
  const [worksheets, setWorksheets] = useState([]);
  const [workbookWorksheets, setWorkbookWorksheets] = useState([]);
  const [tab, setTab] = useState("overview");
  // Allow deep-links like `/modules/{slug}?tab=worksheets` (used by the
  // Course Workbook "Open" CTA) to land on a specific tab. Validated
  // against the known tab keys below; anything else falls back to
  // "overview". Also handles the per-slug reset so the module-switch
  // effect below no longer needs to touch `tab`.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const requested = (searchParams.get("tab") || "").toLowerCase();
    const allowed = new Set(["overview", "read", "checklist", "worksheets", "quiz"]);
    setTab(allowed.has(requested) ? requested : "overview");
  }, [searchParams, slug]);
  const [cert, setCert] = useState(null);
  const [saveWorksheet, setSaveWorksheet] = useState(null);  // worksheet doc being saved to a project
  const [viewWorksheet, setViewWorksheet] = useState(null);  // worksheet doc opened in the in-app viewer
  const [gate, setGate] = useState(null);
  const [markingVideo, setMarkingVideo] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);

  const reloadGate = () => api.get(`/modules/${slug}/gate`)
    .then((r) => setGate(r.data))
    .catch(() => setGate(null));

  useEffect(() => {
    setData(null); setError(null); setLockedGate(null); setWorksheets([]); setWorkbookWorksheets([]); setGate(null);
    api.get(`/modules/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        // Paid-user sequential lock: 403 body is a structured dict
        // `{ code: "module_locked", kind, unlock_after_slug, ... }`.
        // Route it to the locked-gate render path instead of the
        // generic error banner.
        const detail = e?.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.code === "module_locked") {
          setLockedGate(detail);
        } else {
          setError(formatError(e));
        }
      });
    api.get(`/modules/${slug}/worksheets`).then((r) => setWorksheets(r.data || [])).catch(() => setWorksheets([]));
    // Course-workbook (per-slug). Matches `lms_slug` on `workbook_worksheets`.
    api.get("/worksheets", { params: { lms_slug: slug } })
      .then((r) => setWorkbookWorksheets(r.data || []))
      .catch(() => setWorkbookWorksheets([]));
    api.get("/certifications").then((r) => {
      setCert(r.data.find((c) => c.bonus_slug === slug) || null);
    }).catch(() => {});
    reloadGate();
  }, [slug]);

  const reloadModule = () => Promise.all([
    api.get(`/modules/${slug}`).then((r) => setData(r.data)).catch(() => {}),
    reloadGate(),
  ]);

  const markVideoWatched = async () => {
    setMarkingVideo(true);
    try {
      await api.post(`/modules/${slug}/watched`);
      toast.success("Video marked as watched");
      await reloadGate();
    } catch (e) { toast.error(formatError(e)); }
    finally { setMarkingVideo(false); }
  };

  const markSectionRead = async () => {
    setMarkingRead(true);
    try {
      await api.post(`/modules/${slug}/read`);
      toast.success("Section marked as read");
      await reloadGate();
    } catch (e) { toast.error(formatError(e)); }
    finally { setMarkingRead(false); }
  };

  const toggleComplete = async () => {
    try {
      if (data?.my_progress?.completed_at) {
        await api.delete(`/modules/${slug}/complete`);
        toast.success("Module marked as not complete");
      } else {
        await api.post(`/modules/${slug}/complete`);
        toast.success("Module marked complete");
      }
      await reloadModule();
    } catch (e) { toast.error(formatError(e)); }
  };

  const extras = MODULE_EXTRAS[slug] || {};
  const [videoDuration, setVideoDuration] = useState(null);
  // For Vimeo iframes / embed pastes the browser can't see the iframe
  // contents, so `onLoadedMetadata` never fires. `useVideoDuration`
  // hits Vimeo's public oEmbed endpoint to get the authoritative
  // duration for the currently-configured `data.video_url`; when the
  // admin swaps the source clip the hook re-runs and the value
  // refreshes automatically. Returns { duration, loading, source }.
  const vimeoDurationState = useVideoDuration(data?.video_url);

  const formatDuration = (secs) => {
    if (!secs || !isFinite(secs)) return null;
    const total = Math.round(secs);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s.toString().padStart(2, "0")}s`;
    return `${s}s`;
  };

  if (lockedGate) {
    // Paid-user sequential lock. `bonus_gated` shows the "all 10 cores"
    // copy; `sequential_core` shows the specific "Complete Module X"
    // copy with a Go-to-previous CTA that jumps to the immediately
    // previous incomplete module.
    const isBonus = lockedGate.kind === "bonus_gated";
    return (
      <AppShell title="Module locked">
        <Link to="/modules" className="text-sm font-semibold text-shell-side inline-flex items-center gap-1" data-testid="module-back">
          <ArrowLeft className="w-4 h-4" /> All modules
        </Link>
        <div className="mt-8 surface p-10 text-center max-w-xl mx-auto" data-testid="module-locked-page">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary grid place-items-center mx-auto">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="font-display text-3xl mt-5" data-testid="module-locked-page-heading">
            {isBonus ? "Bonus Modules Locked" : "Module Locked"}
          </h1>
          <p className="text-sm text-muted-foreground mt-3" data-testid="module-locked-page-body">
            {isBonus
              ? "Complete all 10 Core Modules to unlock the Bonus Modules."
              : `Complete Module ${lockedGate.unlock_after_number} to unlock Module ${lockedGate.current_number}.`}
          </p>
          {!isBonus && (
            <Link
              to={`/modules/${lockedGate.unlock_after_slug}`}
              className="btn-primary mt-6 inline-flex"
              data-testid="module-locked-page-goto"
            >
              Go to Module {lockedGate.unlock_after_number}
            </Link>
          )}
        </div>
      </AppShell>
    );
  }

  if (error) return (
    <AppShell title="Module">
      <Link to="/modules" className="text-sm font-semibold text-shell-side inline-flex items-center gap-1" data-testid="module-back">
        <ArrowLeft className="w-4 h-4" /> All modules
      </Link>
      <div className="mt-8 surface p-10 text-center max-w-xl">
        <Lock className="w-6 h-6 text-primary mx-auto" />
        <h1 className="font-display text-3xl mt-4">Locked</h1>
        <p className="text-sm text-muted-foreground mt-2">{error}</p>
        <Link to="/account/subscription" className="btn-primary mt-6 inline-flex" data-testid="module-upgrade">Upgrade plan</Link>
      </div>
    </AppShell>
  );

  if (!data) return <AppShell title="Module"><div className="text-muted-foreground">Loading…</div></AppShell>;

  return (
    <AppShell title={data.name}>
      <div className="w-full p-4 md:p-6 lg:p-8" data-testid="module-detail-page">
        <Link to="/modules" className="text-sm font-semibold text-shell-side inline-flex items-center gap-1 mb-4" data-testid="module-back">
          <ArrowLeft className="w-4 h-4" /> All modules
        </Link>

        {(() => {
          // Prefer admin-edited `lesson` and `track` strings; fall back to
          // Core/Bonus and the hardcoded `extras.lessons` count only when the
          // admin has not set a value in the Module Editor.
          const isBonus = String(data.track || "").toLowerCase() === "bonus";
          const trackLabel = (data.track && String(data.track).trim())
            ? String(data.track).trim().replace(/^./, (c) => c.toUpperCase())
            : (isBonus ? "Bonus module" : "Core module");
          const lessonLabel = (data.lesson && String(data.lesson).trim())
            ? String(data.lesson).trim()
            : (extras.lessons ? `${extras.lessons} lessons` : null);
          const eyebrow = [
            trackLabel + (isBonus ? " · Specialist certification" : ""),
            lessonLabel,
            extras.duration || null,
          ].filter(Boolean).join(" · ");
          return (
        <PageHero
          eyebrow={eyebrow}
          icon={isBonus ? Award : BookOpen}
          title={data.name}
          testid="module"
        >
          <AdaptedText
            as="p" className="mt-3 text-muted-foreground max-w-2xl"
            source="lms_module" sourceId={slug} field="description"
            originalText={data.description}
            data-testid="module-description"
          />
        </PageHero>
          );
        })()}

        <div className="mt-8 border-b border-shell-border sticky top-[64px] md:top-[68px] z-20 bg-white/95 backdrop-blur -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8" data-testid="module-tabs">
          <div className="flex gap-1 overflow-x-auto overflow-y-hidden -mx-1 px-1">
            {[
              ["overview","Overview",BookOpen],
              ["read","Read the module",FileText],
              ...(getChecklist(slug).length > 0 ? [["checklist","To-do checklist",ListChecks]] : []),
              ["worksheets","Worksheets",FileText],
              ...(data.has_quiz ? [["quiz","Certification Quiz",ClipboardCheck]] : []),
            ].map(([k, label, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`module-tab-${k}`}
                className={["relative inline-flex items-center gap-2 px-4 py-3 text-sm transition-colors whitespace-nowrap",
                  tab === k ? "text-sky-500 font-semibold" : "text-muted-foreground hover:text-foreground"].join(" ")}>
                <Icon className="w-4 h-4" /> {label}
                {k === "quiz" && data.my_progress?.quiz_passed_at && (
                  <CircleCheck className="w-3.5 h-3.5 text-green-600" />
                )}
                {tab === k && <span className="absolute left-2 right-2 -bottom-px h-0.5 bg-sky-500 shadow-[0_0_8px_rgba(14,165,233,0.6)]" />}
              </button>
            ))}
          </div>
        </div>

        {tab === "overview" && (
          <div className="mt-8">
            {cert && (
              <div className="mb-6 p-4 rounded-xl bg-shell-accent/15 border border-shell-accent/30 inline-flex items-center gap-3" data-testid="module-cert">
                <div className="text-3xl">{cert.emoji}</div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-shell-side font-semibold flex items-center gap-1">
                    <Trophy className="w-3 h-3" /> Certification
                  </p>
                  <p className="font-semibold mt-0.5 text-sm leading-tight">{cert.name}</p>
                </div>
              </div>
            )}
            {/* Grid promotes to two columns only at lg+ (≥1024px). On
                tablets/mobile the video is full-width and the summary
                stacks below at full width — a squeezed 1/4-width aside
                on tablet was cramped and made the copy overlap the stats. */}
            <div className="grid gap-6 lg:grid-cols-4">
              <div className="lg:col-span-3 min-w-0 space-y-4 md:space-y-5">
                {data.video_url ? (
                  // Route the module video through `normalizeVideoUrl`
                  // so Vimeo / YouTube / iframe-paste URLs render in
                  // an `<iframe>` while direct MP4/WEBM/MOV assets
                  // keep the native `<video>` player + progress
                  // metadata capture. This is what enables the new
                  // "Vimeo Embed Code" admin option to work on the
                  // course-player surface without duplicating logic.
                  (() => {
                    const parsed = normalizeVideoUrl(data.video_url);
                    if (parsed.kind === "embed") {
                      // Admin-authored Vimeo embed HTML — render
                      // verbatim so the outer `<div>` (with Vimeo's
                      // padding-based 16:9 ratio) + `<script>` both
                      // survive. Admin-only content.
                      return (
                        <div
                          className="card-elevated overflow-hidden"
                          data-testid="module-video-wrap"
                        >
                          <div
                            className="w-full [&_iframe]:w-full [&_iframe]:aspect-video"
                            data-testid="module-video-embed"
                            dangerouslySetInnerHTML={{ __html: sanitizeVideoEmbed(parsed.html) }}
                          />
                        </div>
                      );
                    }
                    if (parsed.kind === "iframe") {
                      return (
                        <div className="card-elevated overflow-hidden" data-testid="module-video-wrap">
                          <div className="aspect-video bg-black">
                            <iframe
                              src={parsed.src}
                              title={data.name}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                              className="w-full h-full"
                              data-testid="module-video-iframe"
                            />
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div className="card-elevated overflow-hidden" data-testid="module-video-wrap">
                        <video
                          src={parsed.src}
                          controls
                          preload="metadata"
                          onLoadedMetadata={(e) => setVideoDuration(e.currentTarget.duration)}
                          className="w-full aspect-video bg-black object-contain"
                          data-testid="module-video-player"
                        />
                      </div>
                    );
                  })()
                ) : data.tutorial_url ? (
                  <div className="card-elevated overflow-hidden">
                    <div className="aspect-video">
                      <iframe src={data.tutorial_url} title={data.name} className="w-full h-full" allowFullScreen data-testid="module-video" />
                    </div>
                  </div>
                ) : (
                  <div className="card-elevated p-14 text-center">
                    <PlayCircle className="w-8 h-8 text-primary mx-auto" />
                    <p className="mt-3 text-sm text-muted-foreground">Tutorial video coming soon.</p>
                  </div>
                )}

                {/* Two-part quiz gate — "Mark video watched" toggle */}
                {(data.video_url || data.tutorial_url) && (
                  <GateToggle
                    done={gate?.video_watched}
                    doneAt={gate?.video_watched_at}
                    onClick={markVideoWatched}
                    loading={markingVideo}
                    doneLabel="Video watched"
                    idleLabel="Mark video as watched"
                    hint="Confirms you've watched this module's lesson. Required to open the certification quiz."
                    testid="module-mark-video-watched"
                  />
                )}
              </div>
              <aside className="lg:col-span-1 min-w-0">
                <div className="card-elevated p-5 h-full flex flex-col">
                  <h2 className="font-display text-xl">Module summary</h2>
                  {data.body ? (
                    <p className="mt-3 text-sm text-foreground/90 leading-relaxed" data-testid="module-body">{data.body}</p>
                  ) : (
                    <p className="mt-3 text-sm text-muted-foreground italic">Summary coming soon.</p>
                  )}

                  <dl className="mt-5 pt-4 border-t border-shell-border grid grid-cols-2 lg:grid-cols-1 min-[1400px]:grid-cols-2 gap-x-4 gap-y-3" data-testid="module-details">
                    <DetailStat icon={PlayCircle} label="Video time"
                      value={
                        // Priority order — always PREFER the live source:
                        //   1) `videoDuration` — the browser-measured
                        //      duration from an MP4/WEBM/MOV played
                        //      via the native <video> element
                        //      (populated by onLoadedMetadata below).
                        //   2) `vimeoDurationState.duration` — the
                        //      Vimeo oEmbed response for iframe /
                        //      embed-HTML playback.
                        //   3) `extras.duration` — the launch-day
                        //        MODULE_EXTRAS hardcoded fallback, kept
                        //        so YouTube embeds and offline Vimeo
                        //        API failures still show *something*.
                        //   4) "—" when everything is unknown.
                        formatDuration(videoDuration)
                          || formatDuration(vimeoDurationState.duration)
                          || extras.duration
                          || "—"
                      } />
                    <DetailStat icon={BookOpen} label="Lesson"
                      value={
                        (data.lesson && String(data.lesson).trim())
                          ? String(data.lesson).trim()
                          : (extras.lessons ? `${extras.lessons} lessons` : "—")
                      } />
                    <DetailStat icon={Award} label="Track"
                      value={
                        (data.track && String(data.track).trim())
                          ? String(data.track).trim().replace(/^./, (c) => c.toUpperCase())
                          : (String(data.track || "").toLowerCase() === "bonus" ? "Bonus" : "Core")
                      } />
                    {/* Tier stat removed — plan gating is enforced upstream
                        by /gate and the module list; exposing "Tier N" in
                        the summary was noisy and not useful to the founder. */}
                    {worksheets.length > 0 && (
                      <DetailStat icon={FileText} label="Worksheets"
                        value={`${worksheets.length}`} />
                    )}
                    {data.has_quiz && (
                      <DetailStat icon={ClipboardCheck} label="Quiz"
                        value={data.my_progress?.quiz_passed_at ? "Passed" : "Available"} />
                    )}
                  </dl>
                </div>
              </aside>
            </div>
          </div>
        )}

        {tab === "read" && (
          <div className="mt-6 space-y-4" data-testid="module-reader">
            <ReaderBody data={data} extras={extras} />
            <GateToggle
              done={gate?.reading_read}
              doneAt={gate?.reading_read_at}
              onClick={markSectionRead}
              loading={markingRead}
              doneLabel="Section marked as read"
              idleLabel="Mark section as read"
              hint="Confirms you've read this module's material. Required to open the certification quiz."
              testid="module-mark-section-read"
            />
          </div>
        )}

        {tab === "worksheets" && (
          <div className="mt-6 space-y-8">
            {workbookWorksheets.length > 0 && (
              <div>
                <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                  <ClipboardList className="w-3.5 h-3.5" /> Course workbook · {workbookWorksheets.length} worksheet{workbookWorksheets.length === 1 ? "" : "s"}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="module-workbook-grid">
                  {workbookWorksheets.map((w) => (
                    <Link
                      key={w.worksheet_id}
                      to={`/workbook/${w.worksheet_id}`}
                      className="surface p-5 flex flex-col hover:-translate-y-0.5 transition-transform cursor-default"
                      data-testid={`module-workbook-${w.worksheet_id}`}
                    >
                      <div className="text-[10px] uppercase tracking-widest text-shell-accent">
                        Worksheet {w.worksheet_number}
                      </div>
                      <p className="mt-1.5 font-display text-lg leading-snug">{w.title}</p>
                      <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-3">
                        {w.outcome}
                      </p>
                      <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
                        <span>{w.difficulty} · {w.estimated_time}</span>
                        <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                          Open <ChevronRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {worksheets.length === 0 && workbookWorksheets.length === 0 ? (
              <div className="surface p-10 text-center text-muted-foreground" data-testid="module-worksheets-empty">
                No worksheets attached to this module yet. Admins can attach worksheets from
                <span className="font-semibold text-foreground"> Admin → Worksheets</span> or
                <span className="font-semibold text-foreground"> Admin → Modules → Worksheets tab</span>.
              </div>
            ) : worksheets.length > 0 && (
              <div>
                <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                  <FileText className="w-3.5 h-3.5" /> Additional files
                </div>
                <div className="grid sm:grid-cols-2 gap-4" data-testid="module-worksheets-grid">
                  {worksheets.map((w, i) => (
                    <div key={w.id} className="surface p-6 flex flex-col" data-testid={`module-worksheet-${i}`}>
                      <FileText className="w-5 h-5 text-shell-accent" />
                      <p className="mt-4 font-display text-xl" data-testid={`module-worksheet-title-${i}`}>{w.title}</p>
                      {w.description ? (
                        <p className="text-xs text-muted-foreground mt-1" data-testid={`module-worksheet-desc-${i}`}>{w.description}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">Copy into your workspace or print for a working session.</p>
                      )}
                      {w.original_filename && (
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2 truncate" title={w.original_filename}>
                          {w.original_filename}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => setSaveWorksheet(w)}
                        className="mt-5 self-start btn-amber inline-flex items-center gap-1"
                        data-testid={`module-worksheet-open-${i}`}
                      >
                        Open worksheet <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "quiz" && data.has_quiz && (
          <div className="mt-6">
            {!gate?.quiz_available ? (
              <QuizGateNotice gate={gate} />
            ) : (
              <ModuleQuiz
                slug={slug}
                moduleName={data.name}
                initialProgress={data.my_progress}
                onPassed={reloadModule}
              />
            )}
          </div>
        )}

        {tab === "checklist" && (
          <div className="mt-6">
            <ModuleChecklist
              slug={slug}
              initialChecked={data.my_progress?.checked_task_ids || []}
              onChange={(ids) => {
                // Keep the parent's copy in sync so switching tabs (which
                // unmounts the checklist) doesn't drop the newly-checked ids.
                setData((prev) => prev ? ({
                  ...prev,
                  my_progress: { ...(prev.my_progress || {}), checked_task_ids: ids },
                }) : prev);
              }}
            />
          </div>
        )}
      </div>

      {saveWorksheet && (
        <SaveWorksheetModal
          worksheet={saveWorksheet}
          moduleSlug={slug}
          moduleName={data.name}
          onClose={() => setSaveWorksheet(null)}
          onSaved={(ws) => {
            setSaveWorksheet(null);
            setViewWorksheet(ws);  // open in-app viewer right after save
          }}
        />
      )}

      {viewWorksheet && (
        <WorksheetViewer
          worksheet={viewWorksheet}
          onClose={() => setViewWorksheet(null)}
        />
      )}
    </AppShell>
  );
}

// Full-viewport overlay that embeds the worksheet file inline (PDF, image,
// Office doc via Microsoft Office Online viewer) so the founder never
// leaves the app. Header carries an "Open in new window" pop-up button and
// a Close button.
function WorksheetViewer({ worksheet, onClose }) {
  const fileUrl = `${API}/worksheets/${worksheet.id}/file`;
  const filename = worksheet.original_filename || `${worksheet.title || "worksheet"}.${worksheet.ext || "pdf"}`;
  const ext = (worksheet.ext || "").toLowerCase();
  const isImage = ["png", "jpg", "jpeg", "webp", "gif"].includes(ext);
  const isOffice = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext);
  // CSV / TXT / PDF / MD → browser handles them natively via inline Content-Disposition.

  const [publicUrl, setPublicUrl] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(isOffice);
  const [viewerError, setViewerError] = useState(null);

  useEffect(() => {
    if (!isOffice) return;
    setViewerLoading(true);
    setViewerError(null);
    api.post(`/worksheets/${worksheet.id}/view-url`)
      .then((r) => setPublicUrl(r.data.url))
      .catch((e) => setViewerError(formatError(e)))
      .finally(() => setViewerLoading(false));
  }, [worksheet.id, isOffice]);

  const officeViewerUrl = publicUrl
    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(publicUrl)}`
    : null;

  const openPopup = () => {
    // Pop-up window sized ~ readable but detachable. For Office docs use the
    // signed public URL so the file itself loads without cookies.
    const targetUrl = isOffice && publicUrl ? publicUrl : fileUrl;
    const w = Math.min(1200, window.screen.availWidth - 100);
    const h = Math.min(900, window.screen.availHeight - 100);
    const left = Math.max(0, (window.screen.availWidth - w) / 2);
    const top  = Math.max(0, (window.screen.availHeight - h) / 2);
    window.open(
      targetUrl,
      `worksheet-${worksheet.id}`,
      `popup=yes,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-shell-side/85 backdrop-blur-sm flex flex-col p-4 md:p-8"
      data-testid="worksheet-viewer"
    >
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col flex-1 max-w-6xl w-full mx-auto">
        <div className="px-5 py-3 border-b border-shell-border flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Worksheet</p>
            <h3 className="font-display text-lg truncate" data-testid="worksheet-viewer-title">{worksheet.title}</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={fileUrl}
              download={filename}
              className="text-xs font-semibold border border-shell-border rounded-full px-4 py-2 hover:bg-muted inline-flex items-center gap-1.5"
              data-testid="worksheet-viewer-download"
            >
              <Download className="w-3.5 h-3.5" /> Download
            </a>
            <button
              type="button"
              onClick={openPopup}
              data-testid="worksheet-viewer-popup"
              className="text-xs font-semibold border border-shell-border rounded-full px-4 py-2 hover:bg-muted inline-flex items-center gap-1.5"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open in new window
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="worksheet-viewer-close"
              className="btn-primary text-xs"
            >
              Close
            </button>
          </div>
        </div>
        <div className="flex-1 bg-muted overflow-hidden">
          {isImage ? (
            <div className="w-full h-full grid place-items-center overflow-auto p-4">
              <img src={fileUrl} alt={worksheet.title} className="max-w-full max-h-full object-contain" />
            </div>
          ) : isOffice ? (
            viewerLoading ? (
              <div className="w-full h-full grid place-items-center">
                <div className="text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
                  <p className="mt-3 text-sm text-muted-foreground">Preparing preview…</p>
                </div>
              </div>
            ) : viewerError || !officeViewerUrl ? (
              <div className="w-full h-full grid place-items-center p-8 text-center">
                <div>
                  <FileText className="w-10 h-10 text-muted-foreground mx-auto" />
                  <p className="mt-4 font-display text-xl">Preview unavailable</p>
                  <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                    {viewerError || "Try downloading the file or opening it in a new window."}
                  </p>
                  <div className="mt-6 flex items-center justify-center gap-2">
                    <a href={fileUrl} download={filename} className="btn-primary text-sm inline-flex items-center gap-1.5">
                      <Download className="w-3.5 h-3.5" /> Download
                    </a>
                    <button onClick={openPopup} className="btn-amber text-sm inline-flex items-center gap-1.5">
                      <ExternalLink className="w-3.5 h-3.5" /> Open in new window
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <iframe
                src={officeViewerUrl}
                title={worksheet.title}
                className="w-full h-full bg-white"
                data-testid="worksheet-viewer-iframe"
              />
            )
          ) : (
            <iframe
              src={fileUrl}
              title={worksheet.title}
              className="w-full h-full bg-white"
              data-testid="worksheet-viewer-iframe"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Modal styled to match DatabaseDetail's "Save as list" popup. Asks for a
// name + date, saves a rich "worksheet" project entry to /api/projects, and
// then opens the actual worksheet file in a new tab.
function SaveWorksheetModal({ worksheet, moduleSlug, moduleName, onClose, onSaved }) {
  const defaultName = worksheet.title
    ? `${worksheet.title} — ${moduleName}`
    : `Worksheet — ${moduleName}`;
  const [name, setName] = useState(defaultName);
  const [eventDate, setEventDate] = useState("");
  const [comments, setComments] = useState("");
  const [parentId, setParentId] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Give this a name"); return; }
    setBusy(true);
    try {
      await api.post("/projects", {
        name: name.trim(),
        type: "worksheet",
        worksheet_id: worksheet.id,
        worksheet_title: worksheet.title,
        module_slug: moduleSlug,
        event_date: eventDate || null,
        comments: comments.trim() || null,
        parent_id: parentId || null,
      });
      toast.success("Saved to My Projects");
      // Hand the worksheet back so the parent opens the in-app viewer.
      onSaved?.(worksheet);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-shell-side/60 backdrop-blur-sm grid place-items-center p-4"
      data-testid="worksheet-save-modal"
    >
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-shell-border">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold inline-flex items-center gap-1">
            <FolderPlus className="w-3 h-3" /> Save worksheet to My Projects
          </p>
          <h3 className="font-display text-2xl mt-1">
            <span className="text-primary">{worksheet.title}</span>
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Saves this worksheet under <Link to="/projects" className="underline hover:text-primary">My Projects</Link> so you can pick it up later. The file opens in a new tab after you save.
          </p>
        </div>
        <div className="px-6 py-4 grid gap-4">
          <label className="grid gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Name</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Kettle Chips · LLC vs C-Corp comparison"
              data-testid="worksheet-save-name"
              className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Date</span>
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              data-testid="worksheet-save-date"
              className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
            />
          </label>
          <ProjectFolderPicker
            value={parentId}
            onChange={setParentId}
            testidPrefix="worksheet-save-folder"
          />
          <label className="grid gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Comments (optional)</span>
            <textarea
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Notes on why you're saving this, deadline, teammates involved, etc."
              data-testid="worksheet-save-comments"
              className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40 resize-none"
            />
          </label>
        </div>
        <div className="px-6 py-4 border-t border-shell-border flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="worksheet-save-cancel"
            className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-4 py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="worksheet-save-submit"
            className="btn-primary text-sm disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderPlus className="w-3.5 h-3.5" />}
            {busy ? "Saving…" : "Save & open"}
          </button>
        </div>
      </form>
    </div>
  );
}

function DetailStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 min-w-0">
      <Icon className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <dt className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</dt>
        <dd className="text-sm font-semibold leading-snug truncate" title={typeof value === "string" ? value : undefined}>{value}</dd>
      </div>
    </div>
  );
}


function ReaderBody({ data, extras }) {
  const [view, setView] = React.useState(data.deck_url ? "deck" : "summary");
  const summary = MODULE_SUMMARIES[data.slug];

  return (
    <div className="surface overflow-hidden" data-testid="module-reader-body">
      <div className="px-4 md:px-6 py-3 border-b border-shell-border flex items-center justify-between gap-3 flex-wrap">
        <p className="font-semibold flex items-center gap-2 text-sm md:text-base">
          <BookOpen className="w-4 h-4" /> {data.name} — Reader
        </p>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-shell-border p-0.5 bg-white" data-testid="reader-view-toggle">
            {data.deck_url && (
              <button onClick={() => setView("deck")} data-testid="reader-view-deck"
                className={["text-xs font-semibold rounded-full px-3 py-1.5 transition-colors inline-flex items-center gap-1",
                  view === "deck" ? "bg-shell-side text-white" : "text-muted-foreground hover:text-foreground"].join(" ")}>
                <FileText className="w-3.5 h-3.5" /> Slide deck
              </button>
            )}
            <button onClick={() => setView("summary")} data-testid="reader-view-summary"
              className={["text-xs font-semibold rounded-full px-3 py-1.5 transition-colors inline-flex items-center gap-1",
                view === "summary" ? "bg-shell-side text-white" : "text-muted-foreground hover:text-foreground"].join(" ")}>
              <BookOpen className="w-3.5 h-3.5" /> Detailed summary
            </button>
            <button onClick={() => setView("split")} data-testid="reader-view-split"
              className={["text-xs font-semibold rounded-full px-3 py-1.5 transition-colors hidden lg:inline-flex items-center gap-1",
                view === "split" ? "bg-shell-side text-white" : "text-muted-foreground hover:text-foreground"].join(" ")}>
              Side-by-side
            </button>
          </div>
          {/* "Open PDF" link intentionally removed — decks are for in-app
              viewing only. Users cannot pop the raw PDF into a new tab
              (which is the browser's built-in download path). */}
        </div>
      </div>

      {view === "split" && data.deck_url ? (
        <div className="grid lg:grid-cols-2">
          <PdfFlipbook src={data.deck_url} />
          <div className="border-l border-shell-border max-h-[80vh] overflow-y-auto">
            <SummaryBody summary={summary} data={data} extras={extras} compact />
          </div>
        </div>
      ) : view === "deck" && data.deck_url ? (
        <PdfFlipbook src={data.deck_url} />
      ) : (
        <SummaryBody summary={summary} data={data} extras={extras} />
      )}
    </div>
  );
}

function PdfFlipbook({ src }) {
  // Renders the deck via `react-pdf` / pdf.js so it works on every
  // browser + device — including mobile Chrome and iOS Safari, which
  // frequently block or refuse to render inline PDFs delivered via
  // `<iframe src=".pdf">` ("This page has been blocked by Chrome").
  // pdf.js rasterises each page to a `<canvas>` we control, sidesteps
  // the mobile-PDF-viewer gap entirely, and streams through our own
  // fetch (so the tier-gated auth cookie is always attached).
  const { user } = useAuth();
  const [nowTick, setNowTick] = React.useState(() => new Date());
  const [numPages, setNumPages] = React.useState(0);
  const [loadError, setLoadError] = React.useState(false);
  const [renderMode, setRenderMode] = React.useState("pdfjs"); // pdfjs | iframe
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const [pageWidth, setPageWidth] = React.useState(900);

  // Track native fullscreen changes so the toggle icon + wrapper
  // height class stay in sync whether the user exits via our button,
  // Esc, or the browser chrome. Same handler covers `fullscreenchange`
  // and the WebKit-prefixed variant for older iPad Safari.
  React.useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  const toggleFullscreen = React.useCallback(async () => {
    const el = wrapRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        const req = el.requestFullscreen || el.webkitRequestFullscreen;
        if (req) await req.call(el);
      } else {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) await exit.call(document);
      }
    } catch (_) { /* user gesture required / permission denied — no-op */ }
  }, []);

  // Refresh the watermark timestamp every 30 s so screenshots always
  // carry a recent stamp — makes leaked screenshots easier to trace.
  React.useEffect(() => {
    const t = setInterval(() => setNowTick(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Lazy-load handled via static import above; react-pdf is ready
  // immediately so the "Loading deck viewer…" flash never appears.
  // Fit page width to the container so pages render sharp on any viewport.
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth || 900;
      setPageWidth(Math.max(320, Math.min(w - 8, 1400)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!src) return null;
  const directUrl = src.startsWith("http")
    ? src
    : `${API}${src.replace(/^\/api/, "")}`;

  const watermarkLabel = `${user?.email || "member"} · ${formatWatermarkStamp(nowTick)}`;

  // Reusable fullscreen toggle button — same placement in both the
  // pdf.js and iframe branches so the UX is identical regardless of
  // which renderer is active.
  const FullscreenBtn = (
    <button
      type="button"
      onClick={toggleFullscreen}
      title={isFullscreen ? "Exit full-screen" : "Present full-screen"}
      aria-label={isFullscreen ? "Exit full-screen" : "Present full-screen"}
      className="absolute top-2 right-2 z-20 inline-flex items-center gap-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white px-3 py-1.5 text-xs font-semibold backdrop-blur transition-colors"
      data-testid="module-pdf-fullscreen"
    >
      {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
      <span className="hidden sm:inline">{isFullscreen ? "Exit full-screen" : "Present"}</span>
    </button>
  );

  // Emergency fallback: if pdf.js fails (loading, network, canvas
  // rejected by policy), fall back to the direct-URL iframe. On
  // desktop this always works; on mobile the user can still tap the
  // "Open in a new tab" link that renders below.
  if (renderMode === "iframe" || loadError) {
    return (
      <div
        ref={wrapRef}
        className={cn(
          "bg-[#3a3a3a] relative",
          isFullscreen && "h-screen w-screen",
        )}
        data-testid="module-pdf-book"
      >
        {FullscreenBtn}
        <iframe
          src={`${directUrl}#view=FitH&toolbar=0&navpanes=0`}
          title="Module slide deck"
          className={cn("w-full block", isFullscreen ? "h-screen" : "h-[80vh]")}
          loading="lazy"
          allow="fullscreen"
          data-testid="module-pdf-iframe"
        />
        <DeckWatermark label={watermarkLabel} />
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      className={cn(
        "bg-[#3a3a3a] relative overflow-y-scroll [scrollbar-gutter:stable]",
        isFullscreen ? "h-screen w-screen max-h-none" : "max-h-[80vh]",
      )}
      data-testid="module-pdf-book"
    >
      {FullscreenBtn}
      <PdfDocument
        file={directUrl}
        options={REACT_PDF_OPTIONS}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        onLoadError={() => setRenderMode("iframe")}
        loading={
          <div className="w-full min-h-[60vh] flex items-center justify-center text-white/80 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading first page…
          </div>
        }
        error={
          <div className="w-full min-h-[60vh] flex flex-col items-center justify-center text-white/80 text-sm gap-3 px-6 text-center">
            <p>We couldn't render the slide deck here. Opening it in a new tab as a fallback…</p>
          </div>
        }
      >
        {Array.from({ length: numPages }, (_, i) => (
          <LazyPage key={i} index={i} pageWidth={pageWidth} eager={i < 2} />
        ))}
      </PdfDocument>
      <DeckWatermark label={watermarkLabel} />
    </div>
  );
}

/**
 * LazyPage — renders a `<PdfPage>` only when its slot scrolls near
 * the viewport (via IntersectionObserver). This keeps first-paint
 * ultra-cheap: only pages 1-2 rasterize immediately, everything else
 * shows a lightweight placeholder until the user scrolls to it. On a
 * 12-slide deck this drops "everything visible" from ~2-3 s down to
 * <400 ms because pdf.js no longer has to decode 12 canvases up-front.
 * `eager={true}` on the first N pages skips the observer so page 1
 * renders on the first frame with zero scroll interaction.
 */
function LazyPage({ index, pageWidth, eager }) {
  const [visible, setVisible] = React.useState(!!eager);
  const holderRef = React.useRef(null);
  React.useEffect(() => {
    if (visible) return;
    const el = holderRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    // rootMargin=600px so pages one screen-height ahead start
    // rendering while the user is still on the previous slide — the
    // next page is ready by the time they scroll to it.
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisible(true);
        io.disconnect();
      }
    }, { rootMargin: "600px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  return (
    <div
      ref={holderRef}
      className="mx-auto bg-[#3a3a3a] block leading-none"
      style={{ width: pageWidth, minHeight: pageWidth * 0.5625 }}
      data-testid={`module-pdf-page-slot-${index}`}
    >
      {visible ? (
        <PdfPage
          pageNumber={index + 1}
          width={pageWidth}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          loading={
            <div className="w-full h-full bg-[#3a3a3a]" />
          }
        />
      ) : (
        <div className="w-full h-full bg-[#3a3a3a]" />
      )}
    </div>
  );
}

// pdf.js loading options — passed via `Document`'s `options` prop.
// Hoisted so the reference is stable across renders (avoids react-pdf's
// "options changed" console warning + unnecessary reloads).
const REACT_PDF_OPTIONS = {
  cMapUrl: "/pdfjs/cmaps/",
  cMapPacked: true,
  withCredentials: true,
};

// Repeated diagonal watermark overlay sitting on top of the embedded PDF.
// `pointer-events-none` keeps the iframe interactive (page nav, zoom).
// The tile is intentionally sparse and semi-transparent so it doesn't
// wreck reading, but is still visible in any screenshot.
function DeckWatermark({ label }) {
  return (
    <div
      className="absolute inset-0 pointer-events-none select-none overflow-hidden"
      data-testid="module-deck-watermark"
      aria-hidden="true"
    >
      <div
        className="absolute inset-0"
        style={{
          transform: "rotate(-24deg)",
          transformOrigin: "center",
          // Repeat the label as a giant grid of translucent text stamps.
          // Using CSS grid so the pattern re-flows with the viewport.
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gridAutoRows: "120px",
          alignItems: "center",
          justifyItems: "center",
          padding: "40px",
          width: "140%",
          height: "140%",
          left: "-20%",
          top: "-20%",
          color: "rgba(15, 27, 54, 0.10)",
          fontSize: "12px",
          fontWeight: 700,
          letterSpacing: "0.08em",
          whiteSpace: "nowrap",
        }}
      >
        {Array.from({ length: 60 }).map((_, i) => (
          <span key={i}>{label}</span>
        ))}
      </div>
    </div>
  );
}

function formatWatermarkStamp(d) {
  try {
    // Local time — short + parseable back to a session window.
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return d.toISOString();
  }
}

function SummaryBody({ summary, data, extras, compact = false }) {
  if (summary) {
    return (
      <article className={["prose max-w-none leading-relaxed", compact ? "px-5 py-6" : "px-6 md:px-10 py-8"].join(" ")}
        data-testid="module-summary-rich">
        <p className="text-[10px] uppercase tracking-widest text-primary font-semibold">{summary.kicker}</p>
        <p className="mt-3 text-foreground/90">{summary.intro}</p>
        {summary.sections.map(({ h, body }) => (
          <section key={h} className="mt-7">
            <h3 className="font-display text-xl">{h}</h3>
            {body.map((para, i) => (
              <p key={i} className="mt-2 text-foreground/90">{para}</p>
            ))}
          </section>
        ))}
      </article>
    );
  }

  // Fallback for modules that don't yet have a rich summary
  const paras = [
    { h: "Overview", p: data.body || data.description },
    { h: "What you'll learn",
      p: `This module walks you through ${extras.lessons || "several"} lessons across ${extras.duration || "a focused study session"}. You'll finish with practical worksheets you can use immediately on your brand.` },
    { h: "How to work through it",
      p: "Read each section, pause to complete the worksheet, then return to the video to lock in the concept. The Tools tab has the calculators used in this module." },
    { h: "Assessment",
      p: data.track === "bonus"
        ? "Complete the end-of-module assessment to earn your specialist certification. All questions are drawn from the module content."
        : "Complete the end-of-module quiz. All core-module quizzes plus the 50-question final exam earn the Certified CPG Founder™." },
  ];
  return (
    <article className={["prose max-w-none leading-relaxed", compact ? "px-5 py-6" : "px-6 md:px-10 py-8"].join(" ")}>
      {paras.map(({ h, p }) => (
        <section key={h} className="mb-6">
          <h3 className="font-display text-xl">{h}</h3>
          <p className="mt-2 text-foreground/90">{p}</p>
        </section>
      ))}
    </article>
  );
}


// ─── Two-part quiz gate helpers (Phase 1) ─────────────────────────────
// Both live at the bottom of this file to keep the render tree readable.

/**
 * GateToggle — matches the "Add note to self" amber-style CTA used elsewhere.
 * `done=true` renders a compact green confirmation with the checkbox time.
 * `done=false` renders the primary CTA.
 */
function GateToggle({ done, doneAt, onClick, loading, doneLabel, idleLabel, hint, testid }) {
  if (done) {
    return (
      <div
        className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 flex items-center gap-3"
        data-testid={testid}
      >
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        <div className="text-sm">
          <p className="font-semibold text-emerald-800">{doneLabel}</p>
          {doneAt && (
            <p className="text-[11px] text-emerald-700/80 mt-0.5">
              Confirmed {new Date(doneAt).toLocaleString()}
            </p>
          )}
        </div>
      </div>
    );
  }
  return (
    <div
      className="rounded-xl border border-primary/25 bg-primary/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
      data-testid={testid}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        data-testid={`${testid}-button`}
        className="btn-amber inline-flex items-center gap-1.5 text-sm whitespace-nowrap shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Marking…</>
          : <><CheckCircle2 className="w-4 h-4" /> {idleLabel}</>}
      </button>
      {hint && <p className="text-[11.5px] text-muted-foreground flex-1 min-w-0">{hint}</p>}
    </div>
  );
}

/**
 * QuizGateNotice — shown when the user tries the Quiz tab before the two
 * prerequisites are marked, OR before the previous core module's quiz is
 * passed.
 */
function QuizGateNotice({ gate }) {
  if (!gate) return null;
  const missing = [];
  if (!gate.module_unlocked) missing.push(
    gate.track === "bonus"
      ? "Pass every core module quiz first (bonus modules unlock once all 10 core modules are done)."
      : `Pass the previous module's quiz first (${prettySlug(gate.prev_slug)}).`,
  );
  if (!gate.video_watched) missing.push("Mark the video as watched.");
  if (!gate.reading_read)  missing.push("Mark the reading section as read.");
  return (
    <div className="card-elevated p-6" data-testid="module-quiz-gate">
      <div className="flex items-center gap-2">
        <Lock className="w-5 h-5 text-primary" />
        <h3 className="font-display text-xl">Quiz not yet unlocked</h3>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        Before you can take the certification quiz, complete the steps below.
        Each one only takes a click once you've finished the material.
      </p>
      <ul className="mt-4 space-y-2">
        {missing.map((m, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Lock className="w-3.5 h-3.5 text-muted-foreground mt-1 shrink-0" />
            <span>{m}</span>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-[11.5px] text-muted-foreground">
        Pass at 80% or higher to complete this module and unlock the next one.
      </p>
    </div>
  );
}

function prettySlug(slug) {
  if (!slug) return "the previous module";
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
